import {
  CreateRestaurantRequestInterface,
  EditRestaurantRequestInterface,
  GalleryImagesUploadInterface,
  ProfileImagesUploadInterface,
  RestaurantDetailsInterface,
  RestaurantImagesInterface
} from '../../../types/RestaurantInterface';
import { RestaurantSocialsInterface } from '../../../types/RestaurantSocialsInterface';
import { BlobWrapper } from '../../../types/MediaInterface';
import { AlbumMediaInterface } from '../../../types/AlbumMediaInterface';
import { ImageInterface } from '../../../types/ImageInterface';
import { AlbumInterface } from '../../../types/AlbumInterface';
import { buildRestaurantHours } from '../../../utils/time';
import { RestaurantWizardFormValues } from './restaurantWizardSchema';

const hasReorder = (
  array1: (BlobWrapper | AlbumMediaInterface)[],
  array2: (BlobWrapper | AlbumMediaInterface)[]
): boolean =>
  array1?.some(
    (val, index) => (val as AlbumMediaInterface)?.mediaID !== (array2[index] as AlbumMediaInterface)?.mediaID
  ) ?? false;

const buildReordered = (uploaded: (BlobWrapper | AlbumMediaInterface)[]): (number | string | undefined)[] => {
  let fileCount = 0;
  return uploaded.map((val) => {
    if (!('file' in val)) {
      return val.mediaID;
    }

    if (!(val as BlobWrapper).file?.type?.includes('image')) {
      return undefined;
    }

    return `filename-${fileCount++}`;
  });
};

export const keepUnlessDeleted = (
  image: ImageInterface | null | undefined,
  idsToDelete: number[]
): ImageInterface | null => (image?.imageID != null && idsToDelete.includes(image.imageID) ? null : image ?? null);

const filterDeletedItems = <T>(idsToDelete: number[], items: T[], getId: (item: T) => number): T[] => {
  if (!idsToDelete?.length) return items;
  return items.filter((item) => !idsToDelete.includes(getId(item)));
};

export const extractNewImageBlobs = (uploadedMedia: (BlobWrapper | AlbumMediaInterface)[]): Blob[] =>
  uploadedMedia
    .filter((m): m is BlobWrapper => 'file' in m && !!(m as BlobWrapper).file?.type?.includes('image'))
    .map((m) => m.file as Blob);

export const extractNewMediaBlobs = (uploadedMedia: (BlobWrapper | AlbumMediaInterface)[]): Blob[] =>
  uploadedMedia
    .filter((m): m is BlobWrapper => 'file' in m && !!(m as BlobWrapper).file)
    .map((m) => m.file as Blob);

const mapProfileImagesToAlbumMedia = (profileImages: ImageInterface[] = []): AlbumMediaInterface[] =>
  profileImages.map((image) => ({
    mediaID: image.imageID,
    mediaURL: image.imageURL,
    type: 'image'
  }));

const findRemovedProfileIDs = (existingImages: ImageInterface[], retainedMedia: AlbumMediaInterface[]): number[] =>
  existingImages
    .filter((image) => !retainedMedia.some((retained) => retained.mediaID === image.imageID))
    .map((image) => image.imageID)
    .filter((id): id is number => id != null);

const findRemovedIDs = (existingMedia: AlbumMediaInterface[], retainedMedia: AlbumMediaInterface[]): number[] =>
  existingMedia.filter((m) => !retainedMedia.some((r) => r.mediaID === m.mediaID)).map((m) => m.mediaID);

const buildProfileReordered = (uploaded: (BlobWrapper | AlbumMediaInterface)[]): (number | string | undefined)[] => {
  let fileCount = 0;
  return uploaded.map((val) => {
    if (!('file' in val)) {
      return val.mediaID;
    }

    return `filename-${fileCount++}`;
  });
};

export const uploadGalleryMedia = async (
  existingMedia: AlbumMediaInterface[],
  uploadedMedia: (BlobWrapper | AlbumMediaInterface)[]
): Promise<GalleryImagesUploadInterface> => {
  const hasUploads = uploadedMedia?.some((m) => 'file' in m);

  if (hasUploads) {
    const retained = uploadedMedia.filter((m): m is AlbumMediaInterface => !('file' in m));
    return {
      newGalleryImages: extractNewImageBlobs(uploadedMedia),
      newGalleryImagesToDelete: existingMedia?.length ? findRemovedIDs(existingMedia, retained) : [],
      newGalleryOrder: buildReordered(uploadedMedia) as string[]
    };
  }

  const retained = uploadedMedia as AlbumMediaInterface[];
  const hasDelete = !!existingMedia?.length && retained.length !== existingMedia.length;
  const deletedIDs = hasDelete ? findRemovedIDs(existingMedia, retained) : [];

  return {
    newGalleryImages: [],
    newGalleryImagesToDelete: deletedIDs,
    newGalleryOrder:
      hasDelete || hasReorder(existingMedia, uploadedMedia) ? (buildReordered(uploadedMedia) as string[]) : []
  };
};

export const uploadProfileMedia = async (
  existingProfile: ImageInterface[],
  uploadedMedia: (BlobWrapper | AlbumMediaInterface)[]
): Promise<ProfileImagesUploadInterface> => {
  const existingMedia = mapProfileImagesToAlbumMedia(existingProfile);
  const hasUploads = uploadedMedia?.some((m) => 'file' in m);

  if (hasUploads) {
    const retained = uploadedMedia.filter((m): m is AlbumMediaInterface => !('file' in m));
    return {
      newProfileImages: extractNewMediaBlobs(uploadedMedia),
      newProfileImagesToDelete: existingProfile?.length ? findRemovedProfileIDs(existingProfile, retained) : [],
      newProfileOrder: buildProfileReordered(uploadedMedia) as string[]
    };
  }

  const retained = uploadedMedia as AlbumMediaInterface[];
  const hasDelete = !!existingProfile?.length && retained.length !== existingProfile.length;
  const deletedIDs = hasDelete ? findRemovedProfileIDs(existingProfile, retained) : [];

  return {
    newProfileImages: [],
    newProfileImagesToDelete: deletedIDs,
    newProfileOrder:
      hasDelete || hasReorder(existingMedia, uploadedMedia) ? (buildProfileReordered(uploadedMedia) as string[]) : []
  };
};

export const filterDeletedGalleryMedia = (
  galleryImagesToDelete: number[],
  albumMedia: AlbumMediaInterface[]
): AlbumMediaInterface[] => filterDeletedItems(galleryImagesToDelete, albumMedia, (m) => m?.mediaID);

export const filterDeletedProfileImages = (
  imagesToDelete: number[],
  currentProfile: ImageInterface[]
): ImageInterface[] => filterDeletedItems(imagesToDelete, currentProfile, (img) => img?.imageID);

export const buildReorderedGalleryAlbums = (
  images: RestaurantImagesInterface | null,
  galleryOrder: string[],
  currentAlbum: AlbumInterface | undefined
): AlbumInterface[] => {
  const galleryImagesToReorder: AlbumMediaInterface[] = images?.albums?.[0]?.media ?? [];

  const ordered = galleryOrder.map((element) => {
    const id = parseInt(element, 10);
    if (id) return currentAlbum?.media.find((_media) => _media.mediaID === id);
    return galleryImagesToReorder[parseInt(element.split('-')[1], 10)];
  });

  const remaining = galleryImagesToReorder.filter((_media) => !ordered.some((o) => o?.mediaID === _media.mediaID));

  const temp = [...ordered, ...remaining].filter(Boolean) as AlbumMediaInterface[];

  if (temp.length) {
    return [{ ...currentAlbum, media: temp }] as unknown as AlbumInterface[];
  }
  return [{ ...currentAlbum }] as unknown as AlbumInterface[];
};

export const buildReorderedProfileImages = (
  images: RestaurantImagesInterface | null,
  profileOrder: string[],
  currentProfile: ImageInterface[]
): ImageInterface[] => {
  const profileToReorder: ImageInterface[] = images?.profile ?? currentProfile ?? [];

  const ordered = profileOrder.map((element) => {
    const id = parseInt(element, 10);
    if (id) return currentProfile.find((img) => img.imageID === id);
    return profileToReorder[parseInt(element.split('-')[1], 10)];
  });

  const remaining = profileToReorder.filter((img) => !ordered.some((orderedImg) => orderedImg?.imageID === img.imageID));

  return [...ordered, ...remaining].filter(Boolean) as ImageInterface[];
};

export const getImageIdsToDelete = (
  currentImages: RestaurantImagesInterface | undefined,
  logo: Blob | null,
  thumbnail: Blob | null,
  menuCover: Blob | null
): number[] => {
  const ids: (number | undefined)[] = [];
  if (logo && currentImages?.logo) ids.push(currentImages.logo.imageID);
  if (thumbnail && currentImages?.thumbnail) ids.push(currentImages.thumbnail.imageID);
  if (menuCover && currentImages?.menuCover) ids.push(currentImages.menuCover.imageID);
  return ids.filter((id): id is number => id != null);
};

export const buildMergedImages = (
  currentImages: RestaurantImagesInterface | undefined,
  uploadedImages: RestaurantImagesInterface | null,
  imagesToDelete: number[],
  galleryImagesToDelete: number[],
  profileImagesToDelete: number[],
  galleryOrder: string[],
  profileOrder: string[]
): RestaurantImagesInterface | undefined => {
  let result = currentImages;

  if (galleryImagesToDelete?.length || profileImagesToDelete?.length || imagesToDelete?.length) {
    result = {
      albums: [
        {
          ...currentImages?.albums?.[0],
          media: filterDeletedGalleryMedia(galleryImagesToDelete, currentImages?.albums?.[0]?.media ?? [])
        }
      ],
      logo: keepUnlessDeleted(currentImages?.logo, imagesToDelete),
      menuCover: keepUnlessDeleted(currentImages?.menuCover, imagesToDelete),
      profile: filterDeletedProfileImages(profileImagesToDelete, currentImages?.profile ?? []),
      thumbnail: keepUnlessDeleted(currentImages?.thumbnail, imagesToDelete)
    };
  }

  if (uploadedImages || galleryOrder.length || profileOrder.length) {
    result = {
      albums: galleryOrder?.length
        ? buildReorderedGalleryAlbums(uploadedImages, galleryOrder, currentImages?.albums?.[0])
        : result?.albums,
      logo: uploadedImages?.logo ?? result?.logo,
      menuCover: uploadedImages?.menuCover ?? result?.menuCover,
      profile: (() => {
        if (profileOrder?.length) {
          return buildReorderedProfileImages(uploadedImages, profileOrder, currentImages?.profile ?? []);
        }
        if (uploadedImages?.profile?.length) {
          return uploadedImages.profile;
        }
        return result?.profile;
      })(),
      thumbnail: uploadedImages?.thumbnail ?? result?.thumbnail
    };
  }

  return result;
};

export const getSubmitErrorMessage = (error: any): string => {
  if (error?.response?.status === 422)
    return 'The address provided for the restaurant is invalid. Please check the inputted address and try again. If this issue keeps occurring, please reach out to the TapTab team.';
  if (error?.response?.status === 409) return 'Restaurant with the same name already exists at this address.';
  if (error?.response?.status === 400) return error?.response?.data?.errors?.[0]?.message;
  return 'Unexpected error occurred while saving restaurant. Check your input and try again. If this issue keeps occurring please reach out to the TapTab team.';
};

export const buildCreateRestaurantRequest = (
  restaurant: RestaurantWizardFormValues,
  phoneNumber: string
): CreateRestaurantRequestInterface => ({
  name: restaurant.name,
  description: restaurant.description || null,
  primaryTagline: restaurant.primaryTagline || null,
  secondaryTagline: restaurant.secondaryTagline || null,
  phone: phoneNumber,
  email: restaurant.email,
  cuisineID: restaurant.cuisine,
  website: restaurant.website || null,
  address: {
    address1: restaurant.address.address1,
    address2: restaurant.address.address2 || null,
    streetNumber: restaurant.address.address1.split(' ')[0],
    streetName: restaurant.address.address1.split(' ').slice(1).join(' '),
    city: restaurant.address.city,
    governingDistrict: restaurant.address.governingDistrict,
    country: 'United States',
    postalCode: restaurant.address.postCode
  },
  socials: restaurant.socials as RestaurantSocialsInterface,
  restaurantHours: buildRestaurantHours(restaurant.restaurantHours),
  availabilityNotes: restaurant.availabilityNotes || null
});

export const buildEditRestaurantRequest = (
  restaurant: RestaurantWizardFormValues,
  { address: { restaurantAddressID } }: RestaurantDetailsInterface,
  phoneNumber: string
): EditRestaurantRequestInterface => ({
  name: restaurant.name,
  description: restaurant.description,
  primaryTagline: restaurant.primaryTagline,
  secondaryTagline: restaurant.secondaryTagline,
  phone: phoneNumber,
  email: restaurant.email,
  cuisineID: restaurant.cuisine,
  website: restaurant.website,
  address: {
    restaurantAddressID,
    country: 'United States',
    postalCode: restaurant.address.postCode,
    address1: restaurant.address.address1,
    address2: restaurant.address.address2 || null,
    city: restaurant.address.city,
    governingDistrict: restaurant.address.governingDistrict
  },
  socials: { ...restaurant.socials },
  availabilityNotes: restaurant.availabilityNotes,
  restaurantHours: buildRestaurantHours(restaurant?.restaurantHours)
});
